// =============================================================================
// Templates Store - V3-2
// Gestion des modèles de tâches avec optimistic updates
// =============================================================================

import { create } from 'zustand';
import {
  fetchTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  type TaskTemplate,
  type TaskTemplatePayload,
  type CreateTemplateInput,
} from '../data/templateRepository';

interface TemplatesState {
  templates: TaskTemplate[];
  loading: boolean;
  error: string | null;
  
  // Actions
  loadTemplates: (orgId: string, projectId?: string | null) => Promise<void>;
  addTemplate: (input: CreateTemplateInput) => Promise<TaskTemplate>;
  editTemplate: (templateId: string, updates: Partial<Pick<TaskTemplate, 'name' | 'description' | 'payload'>>) => Promise<void>;
  removeTemplate: (templateId: string) => Promise<void>;
  clearTemplates: () => void;
  
  // Helpers
  getTemplateById: (templateId: string) => TaskTemplate | undefined;
  getOrgTemplates: () => TaskTemplate[];
  getProjectTemplates: (projectId: string) => TaskTemplate[];
}

export const useTemplatesStore = create<TemplatesState>((set, get) => ({
  templates: [],
  loading: false,
  error: null,

  loadTemplates: async (orgId, projectId) => {
    set({ loading: true, error: null });
    try {
      const templates = await fetchTemplates(orgId, projectId);
      set({ templates, loading: false });
    } catch (err) {
      console.error('[useTemplatesStore] loadTemplates error:', err);
      set({ error: 'Erreur lors du chargement des modèles', loading: false });
    }
  },

  addTemplate: async (input) => {
    const tempId = `temp-${Date.now()}`;
    const optimisticTemplate: TaskTemplate = {
      id: tempId,
      org_id: input.org_id,
      project_id: input.project_id || null,
      name: input.name,
      description: input.description || null,
      payload: input.payload,
      created_by: input.created_by || null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    // Optimistic add
    set(state => ({
      templates: [...state.templates, optimisticTemplate],
    }));

    try {
      const created = await createTemplate(input);
      
      // Replace temp with real
      set(state => ({
        templates: state.templates.map(t => 
          t.id === tempId ? created : t
        ),
      }));
      
      return created;
    } catch (err) {
      // Rollback
      set(state => ({
        templates: state.templates.filter(t => t.id !== tempId),
      }));
      throw err;
    }
  },

  editTemplate: async (templateId, updates) => {
    const original = get().templates.find(t => t.id === templateId);
    if (!original) return;

    // Optimistic update
    set(state => ({
      templates: state.templates.map(t =>
        t.id === templateId ? { ...t, ...updates, updated_at: new Date().toISOString() } : t
      ),
    }));

    try {
      await updateTemplate(templateId, updates);
    } catch (err) {
      // Rollback
      set(state => ({
        templates: state.templates.map(t =>
          t.id === templateId ? original : t
        ),
      }));
      throw err;
    }
  },

  removeTemplate: async (templateId) => {
    const original = get().templates.find(t => t.id === templateId);
    if (!original) return;

    // Optimistic remove
    set(state => ({
      templates: state.templates.filter(t => t.id !== templateId),
    }));

    try {
      await deleteTemplate(templateId);
    } catch (err) {
      // Rollback
      set(state => ({
        templates: [...state.templates, original],
      }));
      throw err;
    }
  },

  clearTemplates: () => {
    set({ templates: [], loading: false, error: null });
  },

  getTemplateById: (templateId) => {
    return get().templates.find(t => t.id === templateId);
  },

  getOrgTemplates: () => {
    return get().templates.filter(t => t.project_id === null);
  },

  getProjectTemplates: (projectId) => {
    return get().templates.filter(t => t.project_id === projectId);
  },
}));

// Export types
export type { TaskTemplate, TaskTemplatePayload, CreateTemplateInput };
